import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Search,
  X,
  Clock,
  User,
  Shirt,
  Hash,
} from 'lucide-react';
import { useSocialStore } from '@/stores/socialStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { SearchResult } from '@/types';

const typeIcons: Record<string, React.ElementType> = {
  user: User,
  outfit: Shirt,
  garment: Shirt,
  tag: Hash,
};

const typeLabels: Record<string, string> = {
  user: 'People',
  outfit: 'Outfits',
  garment: 'Items',
  tag: 'Tags',
};

export function SearchView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    searchResults,
    recentSearches,
    isLoadingSearch,
    search,
    setSearchQuery,
    clearSearch,
  } = useSocialStore();

  const [localQuery, setLocalQuery] = useState(searchParams.get('q') ?? '');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setLocalQuery(q);
      search(q);
    }
  }, [searchParams, search]);

  const handleSearch = useCallback(
    (query: string) => {
      setLocalQuery(query);
      setSearchQuery(query);
      if (query.trim().length >= 2) {
        const timer = setTimeout(() => search(query), 300);
        return () => clearTimeout(timer);
      }
    },
    [search, setSearchQuery],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (localQuery.trim()) {
        search(localQuery);
      }
    },
    [localQuery, search],
  );

  const handleClear = useCallback(() => {
    setLocalQuery('');
    clearSearch();
    inputRef.current?.focus();
  }, [clearSearch]);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      switch (result.type) {
        case 'user':
          navigate(`/discover/profile/${result.id}`);
          break;
        case 'outfit':
        case 'garment':
          navigate(`/closet/${result.id}`);
          break;
        case 'tag':
          setLocalQuery(`#${result.title}`);
          search(`#${result.title}`);
          break;
      }
    },
    [navigate, search],
  );

  // Group results by type
  const groupedResults = searchResults.reduce<Record<string, SearchResult[]>>(
    (acc, result) => {
      if (!acc[result.type]) acc[result.type] = [];
      acc[result.type].push(result);
      return acc;
    },
    {},
  );

  const showRecentSearches = !localQuery && recentSearches.length > 0;
  const showResults = localQuery && searchResults.length > 0;
  const showEmpty = localQuery && !isLoadingSearch && searchResults.length === 0;

  return (
    <motion.div
      className="min-h-screen bg-vestir-white pb-24"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Search header */}
      <div className="sticky top-0 z-30 bg-vestir-white/90 backdrop-blur-xl border-b border-vestir-gray/30">
        <div className="px-4 pt-12 pb-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/discover')}
            className="w-10 h-10 bg-vestir-gray/40 rounded-full flex items-center justify-center flex-shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>

          <form onSubmit={handleSubmit} className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark"
            />
            <input
              ref={inputRef}
              type="text"
              value={localQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search people, outfits, tags..."
              className="w-full pl-9 pr-9 py-2.5 bg-vestir-gray/40 rounded-xl text-sm
                         placeholder:text-vestir-gray-dark focus:outline-none focus:ring-2
                         focus:ring-vestir-purple/30"
              aria-label="Search"
            />
            <AnimatePresence>
              {localQuery && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  aria-label="Clear search"
                >
                  <X size={16} className="text-vestir-gray-dark" />
                </motion.button>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>

      <div className="px-4 pt-4">
        {/* Recent searches */}
        {showRecentSearches && (
          <div>
            <h3 className="text-xs font-medium text-vestir-gray-dark mb-3 flex items-center gap-1.5">
              <Clock size={12} />
              Recent Searches
            </h3>
            <div className="space-y-1">
              {recentSearches.map((query) => (
                <button
                  key={query}
                  onClick={() => {
                    setLocalQuery(query);
                    search(query);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                             hover:bg-vestir-gray/20 transition-colors text-left"
                >
                  <Clock size={14} className="text-vestir-gray-dark flex-shrink-0" />
                  <span className="text-sm flex-1">{query}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoadingSearch && (
          <div className="py-8">
            <LoadingSpinner size="sm" message="Searching..." />
          </div>
        )}

        {/* Grouped results */}
        {showResults && (
          <div className="space-y-5">
            {Object.entries(groupedResults).map(([type, results]) => {
              const TypeIcon = typeIcons[type] ?? Search;

              return (
                <div key={type}>
                  <h3 className="text-xs font-medium text-vestir-gray-dark mb-2 flex items-center gap-1.5">
                    <TypeIcon size={12} />
                    {typeLabels[type] ?? type}
                    <span className="text-vestir-gray-dark/50">({results.length})</span>
                  </h3>
                  <div className="space-y-1">
                    {results.map((result) => (
                      <motion.button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full flex items-center gap-3 p-2.5 rounded-xl
                                   hover:bg-vestir-gray/20 transition-colors text-left"
                        whileTap={{ scale: 0.98 }}
                      >
                        <img
                          src={result.thumbnailUrl || '/placeholder.png'}
                          alt=""
                          className={`w-10 h-10 object-cover flex-shrink-0 ${
                            result.type === 'user' ? 'rounded-full' : 'rounded-lg'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{result.title}</p>
                          <p className="text-xs text-vestir-gray-dark truncate">
                            {result.subtitle}
                          </p>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div className="text-center py-16">
            <Search size={32} className="mx-auto text-vestir-gray-dark/30 mb-3" />
            <p className="text-sm text-vestir-gray-dark">
              No results for "{localQuery}"
            </p>
            <p className="text-xs text-vestir-gray-dark/70 mt-1">
              Try different keywords or check spelling
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
